/**
 * Marketing list types for API responses
 */

import { BaseResponse, BaseDataResponse, BaseLightResponse } from './common';

/**
 * Filter operators
 */
export enum FilterOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'notEquals',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'notContains',
  STARTS_WITH = 'startsWith',
  ENDS_WITH = 'endsWith',
  IS_EMPTY = 'isEmpty',
  IS_NOT_EMPTY = 'isNotEmpty',
  IN = 'in',
  NOT_IN = 'notIn',
}

/**
 * Numeric operators
 */
export enum NumericOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'notEquals',
  GREATER_THAN = 'greaterThan',
  LESS_THAN = 'lessThan',
  GREATER_THAN_OR_EQUAL = 'greaterThanOrEqual',
  LESS_THAN_OR_EQUAL = 'lessThanOrEqual',
  BETWEEN = 'between',
}

/**
 * Date operators
 */
export enum DateOperator {
  EQUALS = 'equals',
  BEFORE = 'before',
  AFTER = 'after',
  BETWEEN = 'between',
  LAST_7_DAYS = 'last7Days',
  LAST_30_DAYS = 'last30Days',
  LAST_90_DAYS = 'last90Days',
  THIS_MONTH = 'thisMonth',
  LAST_MONTH = 'lastMonth',
  THIS_YEAR = 'thisYear',
  LAST_YEAR = 'lastYear',
}

/**
 * Activity-based operators
 */
export enum ActivityOperator {
  IS_MEMBER_OF_LIST = 'isMemberOfList',
  IS_NOT_MEMBER_OF_LIST = 'isNotMemberOfList',
  HAS_FILLED_FORM = 'hasFilledForm',
  HAS_NOT_FILLED_FORM = 'hasNotFilledForm',
  HAS_OPENED_EMAIL = 'hasOpenedEmail',
  HAS_NOT_OPENED_EMAIL = 'hasNotOpenedEmail',
  HAS_CLICKED_EMAIL = 'hasClickedEmail',
  HAS_NOT_CLICKED_EMAIL = 'hasNotClickedEmail',
  HAS_SUBSCRIPTION = 'hasSubscription',
  HAS_NO_SUBSCRIPTION = 'hasNoSubscription',
  HAS_ACTIVE_SUBSCRIPTION = 'hasActiveSubscription',
  HAS_NO_ACTIVE_SUBSCRIPTION = 'hasNoActiveSubscription',
}

/**
 * Object types that can be filtered
 */
export enum FilterObjectType {
  CONTACT = 'contact',
  ORGANIZATION = 'organization',
  PRODUCT = 'product',
  SKU = 'sku',
  PLAN = 'plan',
  SUBSCRIPTION = 'subscription',
}

/**
 * Property data types
 */
export enum PropertyType {
  STRING = 'string',
  NUMBER = 'number',
  DATE = 'date',
  BOOLEAN = 'boolean',
  REFERENCE = 'reference',
  ARRAY = 'array',
}

/**
 * Date range
 */
/**
 * Possible filter value types
 */
export type FilterValue = string | number | boolean | DateRange | NumericRange | null;

export interface DateRange {
  from?: Date | string;
  to?: Date | string;
}

/**
 * Numeric range
 */
export interface NumericRange {
  from?: number;
  to?: number;
}

/**
 * Individual filter definition - HubSpot style
 */
export interface Filter {
  /** Property path (e.g., 'firstName', 'organization.name') */
  property: string;

  /** Type of property being filtered */
  propertyType: PropertyType;

  /** Object type this property belongs to */
  objectType: FilterObjectType;

  /** Comparison operator */
  operator: FilterOperator | NumericOperator | DateOperator | ActivityOperator | string;

  /** Single value for most operators */
  value?: FilterValue;

  /** Multiple values for IN, NOT_IN operators */
  values?: FilterValue[];

  /** Reference ID for activity operators (e.g., list ID, form ID) */
  referenceId?: string;
}

/**
 * Filter group - combines multiple filters with AND/OR logic
 */
export interface FilterGroup {
  /** Filters in this group */
  filters: Filter[];

  /** How to combine filters within this group */
  logic: 'AND' | 'OR';
}

/**
 * Filter criteria structure for dynamic lists - HubSpot style
 */
export interface FilterCriteria {
  /** Filter groups */
  filterGroups?: FilterGroup[];

  /** How to combine filter groups (AND/OR between groups) */
  groupLogic?: 'AND' | 'OR';
}

/**
 * Property metadata for UI
 */
export interface PropertyMetadata {
  key: string;
  label: string;
  objectType: FilterObjectType;
  propertyType: PropertyType;
  operators: (FilterOperator | NumericOperator | DateOperator | ActivityOperator)[];
  fieldPath: string;
  requiresLookup: boolean;
  referenceCollection?: string;
  searchable?: boolean;
  category?: string;
}

export interface MarketingListsResponse extends BaseResponse<MarketingListDoc> {}

export interface MarketingListLightResponse
  extends BaseDataResponse<MarketingListLightDoc[]> {}

export interface MarketingListLightDoc extends BaseLightResponse {
  description?: string;
  listType: string;
  contactCount: number;
  status: string;
}

export interface MarketingListResponse {
  data: MarketingListData;
}

export interface MarketingListData {
  _id: string;
  name: string;
  description?: string;
  listType: string;
  status: string;
  createdBy?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  organization?: {
    _id: string;
    name: string;
  };
  contacts: string[];
  filterCriteria?: FilterCriteria;
  contactCount: number;
  lastRefreshed?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface MarketingListDoc {
  _id: string;
  name: string;
  description?: string;
  listType: 'static' | 'dynamic';
  status: 'active' | 'inactive' | 'archived';
  createdBy?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  organization?: {
    _id: string;
    name: string;
  };
  contacts: string[];
  filterCriteria?: FilterCriteria;
  contactCount: number;
  lastRefreshed?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export type MarketingListType = 'static' | 'dynamic';
export type MarketingListStatus = 'active' | 'inactive' | 'archived';

export type IMarketingListItem = MarketingListData;

export type IMarketingListTableFilters = {
  search: string;
  listType: string;
  status: string;
  page: number;
  pageSize: number;
};

export type IMarketingListTableFilterValue = string | string[] | number | boolean;

/**
 * Individual filter condition for advanced filters
 */
export interface FilterCondition<T = FilterValue> {
  operator: FilterOperator | NumericOperator | DateOperator | ActivityOperator | string;
  value?: T;
  values?: T[];
}

/**
 * Preview response types
 */
export interface PreviewMarketingListRequest {
  filterCriteria: FilterCriteria;
  sampleSize?: number;
}

export interface PreviewMarketingListResponse {
  count: number;
  percentage: number;
  sampleContacts: PreviewContact[];
  totalContacts: number;
  cached?: boolean;
}

export interface PreviewContact {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  organization?: {
    _id: string;
    name: string;
  };
}

/**
 * Contact returned from list contacts endpoint (more fields than preview)
 */
export interface ListContact {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  organization?: {
    _id: string;
    name: string;
  };
  updatedAt: string;
}

/**
 * Marketing list contacts paginated response
 */
export interface MarketingListContactsResponse {
  docs: ListContact[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * AI Filter Generator types
 */
export interface AiGenerateFiltersRequest {
  prompt: string;
  currentCriteria?: FilterCriteria;
}

export interface AiGenerateFiltersResponse {
  filterCriteria: FilterCriteria;
  explanation: string;
  confidence: number;
}

